import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Grid,
} from "@mui/material";
import debounce from "@mui/utils/debounce";
import {
	BreadcrumbItem,
	BreadcrumbLink,
	Breadcrumbs,
	ConfirmButton,
	View,
	ViewHeader,
} from "@variocube/app-ui";
import {TextField} from "@variocube/app-ui";
import React, {useState} from "react";
import {useAsync} from "react-async-hook";
import {Link, useNavigate} from "react-router-dom";
import {User, useUserApi} from "../../api";
import {LabeledText} from "../../component/labeled-text";
import {useLocalization} from "../../i18n";
import {notBlank} from "../../util";
import {useNumericParam} from "../../utils/useParam";
import {getUserDisplayName} from "../../utils/user";

export function UserMerge() {
	const navigate = useNavigate();
	const {t} = useLocalization();
	const {getUser, queryUsers, mergeUser} = useUserApi();
	const sourceId = useNumericParam("sourceId");

	const {loading, error, result: source} = useAsync(getUser, [sourceId]);

	const [search, setSearch] = useState("");
	const [target, setTarget] = useState<User | null>(null);
	const [merging, setMerging] = useState(false);

	const handleSearchInput = debounce(setSearch, 500);

	// Filter users with compatible roles, excluding the source user
	const {result: options} = useAsync(async () => {
		if (!source) return [];
		const role = isOebbRole(source.role) ? undefined : source.role;
		const result = await queryUsers({search, role, size: 25});
		return (result.content ?? []).filter(u =>
			u.id !== sourceId
			&& !isSuperUserOrApiUser(u.role)
			&& isOebbRole(source.role) === isOebbRole(u.role)
		);
	}, [search, source]);

	async function handleMerge() {
		if (target) {
			setMerging(true);
			try {
				await mergeUser(target.id, sourceId);
				navigate(`/users/${target.id}`);
			} finally {
				setMerging(false);
			}
		}
	}

	return (
		<View loading={loading} error={error}>
			<Breadcrumbs>
				<BreadcrumbItem>{t("navigation.admin")}</BreadcrumbItem>
				<BreadcrumbLink component={Link} to={"/users"}>{t("users.plural")}</BreadcrumbLink>
				<BreadcrumbLink component={Link} to={`/users/${sourceId}`}>{getUserDisplayName(source)}</BreadcrumbLink>
				<BreadcrumbItem>{t("users.merge.title")}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader title={t("users.merge.title")} />
			<Alert severity="info" sx={{mb: 3}}>
				{t("users.merge.info")}
			</Alert>
			<Box>
			<Grid container spacing={3}>
				<Grid item xs={12} md={5}>
					<Card>
						<CardHeader title={t("users.merge.source")} />
						<CardContent>
							{source && <UserDetails user={source} />}
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} md={2} sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
					<Box sx={{fontSize: 32}}>→</Box>
				</Grid>
				<Grid item xs={12} md={5}>
					<Card>
						<CardHeader title={t("users.merge.target")} />
						<CardContent>
							<Autocomplete
								onInputChange={(_, v) => handleSearchInput(v)}
								renderInput={params => <TextField {...params} label={t("users.merge.selectTarget")} />}
								options={options ?? []}
								value={target}
								onChange={(_, v) => setTarget(v)}
								getOptionLabel={o => {
									const labels = [
										[o.lastname, o.firstname].filter(notBlank).join(", "),
										o.email,
									];
									return labels.filter(notBlank).join(" - ");
								}}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								sx={{mb: 2}}
							/>
							{target && <UserDetails user={target} />}
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			</Box>
			<Box sx={{mt: 3, display: "flex", justifyContent: "flex-end"}}>
				<ConfirmButton
					title={t("users.merge.confirm.title")}
					cancel={t("actions.cancel")}
					onConfirm={handleMerge}
					variant="contained"
					color="warning"
					disabled={!target || merging}
				>
					{t("users.merge.confirm.message")}
				</ConfirmButton>
			</Box>
		</View>
	);
}

function UserDetails({user}: { user: User }) {
	const {t} = useLocalization();
	return (
		<Grid container spacing={1}>
			<Grid item xs={6}>
				<LabeledText label={t("common.firstName")} text={user.firstname} />
			</Grid>
			<Grid item xs={6}>
				<LabeledText label={t("common.lastName")} text={user.lastname} />
			</Grid>
			<Grid item xs={6}>
				<LabeledText label={t("common.email")} text={user.email} />
			</Grid>
			<Grid item xs={6}>
				<LabeledText label={t("users.role")} text={t(`users.roles.${user.role}`)} />
			</Grid>
		</Grid>
	);
}

function isOebbRole(role: string) {
	return role === "OebbUser" || role === "OebbAdministrator";
}

function isSuperUserOrApiUser(role: string) {
	return role === "SuperUser" || role === "OebbApiUser";
}
